import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '~/components/ui/button';
import { refreshAuthUser } from '~/features/auth/services/refresh-auth-user';
import { useAuthStore } from '~/store/auth.store';

export function ImpersonationBanner() {
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const context = useAuthStore((s) => s.impersonationContext);
  const stopImpersonation = useAuthStore((s) => s.stopImpersonation);
  const queryClient = useQueryClient();
  const [isStopping, setIsStopping] = useState(false);

  if (!isImpersonating || !context) return null;

  const targetName = [context.targetUser.firstName, context.targetUser.lastName]
    .filter(Boolean)
    .join(' ');
  const displayName = targetName || context.targetUser.email;

  // Restoring the admin token leaves the store holding whatever profile the
  // impersonated session hydrated, so re-hydrate from /auth/me before refetching
  // — otherwise `activeTenantId` keeps describing the target's subsidiary while
  // requests are scoped to the admin's, and every tenant-owned read 404s.
  const handleStop = async () => {
    setIsStopping(true);
    stopImpersonation();
    try {
      await refreshAuthUser();
      await queryClient.invalidateQueries();
      toast.success('Impersonation ended. Restored admin session.');
    } catch {
      // Tokens are already restored; a failed re-hydrate only leaves the store
      // stale, and the `_tenant` guard repairs that on the next navigation.
      toast.error('Impersonation ended, but the session could not be refreshed.');
    } finally {
      setIsStopping(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 bg-amber-500 px-4 py-2 text-sm font-medium text-white">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>
          Impersonating <strong>{displayName}</strong>
        </span>
        {context.reason && <span className="opacity-80">— {context.reason}</span>}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 cursor-pointer gap-1 text-white hover:bg-amber-600 hover:text-white"
        isLoading={isStopping}
        onClick={() => void handleStop()}
      >
        <X className="h-3.5 w-3.5" />
        Stop Impersonating
      </Button>
    </div>
  );
}
